// Emitted in place of a Slack result when the org has no usable Slack
// integration for this project. This is a card, not a failure: the tool
// succeeded in detecting the gap.
export interface SlackSetupRequiredPayload {
  type: "slack_setup_required";
  reason: "workspace_not_configured" | "not_exposed" | "scope_not_authorized";
  capability?: string;
  missingScopes?: string[];
}

const REASONS: ReadonlySet<SlackSetupRequiredPayload["reason"]> = new Set([
  "workspace_not_configured",
  "not_exposed",
  "scope_not_authorized",
]);

const isReason = (v: unknown): v is SlackSetupRequiredPayload["reason"] =>
  typeof v === "string" && REASONS.has(v as SlackSetupRequiredPayload["reason"]);

// Tolerates unknown reasons so a newer backend still renders the card.
export const parseSlackSetupRequiredPayload = (v: unknown): SlackSetupRequiredPayload | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (r.type !== "slack_setup_required") return null;
  return {
    type: "slack_setup_required",
    reason: isReason(r.reason) ? r.reason : "workspace_not_configured",
    ...(typeof r.capability === "string" ? { capability: r.capability } : {}),
    ...(Array.isArray(r.missingScopes) && r.missingScopes.every((s): s is string => typeof s === "string")
      ? { missingScopes: r.missingScopes }
      : {}),
  };
};
